// Default content for size-chart pages, used as fallbacks when no DB records exist
// and as seed data. Matches the spec's required content exactly.

export const DEFAULT_SIZE_ROWS = [
  { us_size: '0', bust: 32, waist: 24, hip: 35, eu_size: '32', uk_size: '4' },
  { us_size: '2', bust: 33, waist: 25, hip: 36, eu_size: '34', uk_size: '6' },
  { us_size: '4', bust: 34, waist: 26, hip: 37, eu_size: '36', uk_size: '8' },
  { us_size: '6', bust: 35, waist: 27, hip: 38, eu_size: '38', uk_size: '10' },
  { us_size: '8', bust: 36, waist: 28, hip: 39, eu_size: '40', uk_size: '12' },
  { us_size: '10', bust: 37, waist: 29, hip: 40, eu_size: '42', uk_size: '14' },
  { us_size: '12', bust: 38.5, waist: 31, hip: 41.5, eu_size: '44', uk_size: '16' },
  { us_size: '14', bust: 40, waist: 33, hip: 43, eu_size: '46', uk_size: '18' },
  { us_size: '16', bust: 42, waist: 35, hip: 45, eu_size: '48', uk_size: '20' },
  { us_size: '18', bust: 44, waist: 37, hip: 47, eu_size: '50', uk_size: '22' },
  { us_size: '20', bust: 46, waist: 39, hip: 49, eu_size: '52', uk_size: '24' },
  { us_size: '22', bust: 48, waist: 41, hip: 51, eu_size: '54', uk_size: '26' },
];

export const DEFAULT_HOW_TO_MEASURE = {
  title: 'How to Measure Yourself for a Prom Dress | Laura\'s Boutique & Bridal',
  intro_text:
    "Getting the right measurements is the key to finding a prom dress that fits beautifully from the start. Use a soft measuring tape and, if possible, have a friend help you for the most accurate results. Measure over your regular bra or the type of undergarment you plan to wear with your dress, and stand naturally — don't hold your breath or pull the tape too tight.",
  steps: [
    {
      step_title: 'Bust',
      step_description:
        "Wrap the tape around the fullest part of your bust, keeping it level and parallel to the floor all the way around your back. Keep the tape snug but not tight — you should be able to slide a finger underneath it comfortably.",
    },
    {
      step_title: 'Waist',
      step_description:
        "Find your natural waistline (the narrowest part of your torso, usually just above your belly button). Wrap the tape around this point, keeping it level. Don't suck in — measure as you'd naturally stand.",
    },
    {
      step_title: 'Hip',
      step_description:
        "Stand with your feet together and wrap the tape around the fullest part of your hips and rear, usually about 7–9 inches below your natural waistline. Keep the tape level around your entire body.",
    },
  ],
  tips: [
    "Measure yourself in your underwear or light clothing for the most accurate numbers — thick clothing can add extra inches.",
    "If your measurements fall between two sizes on a designer's chart, size up rather than down — dresses are almost always easier to take in than to let out.",
    "Every designer fits differently, so always check the specific size chart for the dress you're ordering rather than relying on your everyday clothing size.",
    "Take your measurements again close to your prom date if you're ordering dresses far in advance, since bodies can change.",
    "When in doubt, contact Laura's Boutique & Bridal directly — we're happy to help you find your perfect fit.",
  ],
  faqs: [
    {
      question: 'What should I wear while measuring myself?',
      answer: "Wear the undergarments you plan to wear with your dress, or light-fitting clothing, for the most accurate results.",
    },
    {
      question: 'Do I need help measuring myself, or can I do it alone?',
      answer: "You can measure yourself alone, but having a friend help hold the tape level around your back will give more accurate results.",
    },
    {
      question: 'How often should I re-measure before ordering a dress?',
      answer: "If you're ordering more than a few months in advance, re-measure closer to your prom date since bodies can change.",
    },
    {
      question: 'What if my measurements don\'t match any single size on the chart?',
      answer: "Order based on your largest measurement, or contact Laura's Boutique & Bridal directly for guidance on your specific measurements.",
    },
  ],
};

export const DEFAULT_INDEX_PAGE = {
  intro_text:
    "Finding the right prom dress starts with the right measurements — and every designer uses their own size specifications. Below you'll find size charts for every prom dress designer carried at Laura's Boutique & Bridal, from Dream Couture by Laura Rudovic to Jovani, La Diviné, Andrea & Leo, and more. Use these charts alongside our measuring guide to find your perfect fit, and contact us any time for personalized sizing guidance at our Carmel, NY atelier.",
  faqs: [
    {
      question: 'Why do sizes vary between prom dress designers?',
      answer:
        "Each designer uses its own size specifications, so a size 6 in one brand may fit differently than a size 6 in another. Always check the specific designer's chart rather than relying on your everyday clothing size.",
    },
    {
      question: 'How do I find my designer\'s size chart?',
      answer:
        "Search or browse the list above by designer name, or visit the designer's page directly and click \"View Size Chart & Fit Guide.\"",
    },
    {
      question: 'What if my designer isn\'t listed here?',
      answer:
        "Contact Laura's Boutique & Bridal directly, and we'll help you find the right size for any dress in our collection.",
    },
  ],
};

// Default FAQs for each designer size-chart page (with [Designer Name] placeholder)
export const DEFAULT_DESIGNER_FAQS = (designerName) => [
  {
    question: `Does ${designerName} run true to size, small, or large?`,
    answer: `${designerName} sizing generally runs true to size. If you're between sizes, we recommend sizing up, since dresses are easier to take in than let out.`,
  },
  {
    question: 'What if my measurements fall between two sizes?',
    answer:
      "Choose the larger size. A dress that's slightly big can be altered to fit perfectly, but one that's too small often can't be let out enough, especially with structured or beaded fabrics.",
  },
  {
    question: 'Do I need to add extra room for a corset or zipper back?',
    answer:
      "For corset-back styles, you can typically size down slightly since the back laces adjust for a closer fit. For zipper-back styles, order based on your exact measurements without adjusting.",
  },
  {
    question: 'Can this dress be altered if it doesn\'t fit perfectly?',
    answer:
      "Yes, most prom dresses can be professionally altered for fit. Contact Laura's Boutique & Bridal to discuss alteration options for your specific dress.",
  },
  {
    question: 'How do I know which measurement to prioritize if my bust, waist, and hip fall into different sizes?',
    answer:
      "Generally, order based on your largest measurement and plan for alterations to take in the smaller areas.",
  },
];

export const CONDENSED_MEASURE_SUMMARY =
  "Use a soft measuring tape and measure over the undergarments you plan to wear. For your bust, wrap the tape around the fullest part, keeping it level. For your waist, measure the narrowest part of your torso, usually just above your belly button. For your hips, measure the fullest part, about 7–9 inches below your waist. If you're between sizes, size up.";